import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, TitleStrategy, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import * as appEffects from '@app/app.effects';
import { reducers } from '@app/app.reducer';
import { routes } from '@app/app.routes';
import { ColorizerEffect } from '@app/colorizer/colorizer.effects';
import { PressNewsEffect } from '@app/press-news/press-news.effects';
import { provideLimestone } from '@axess/ng-limestone';
import { AccessibilityService } from '@core/data-access/services/accessibility/accessibility.service';
import { ConfigReload } from '@core/data-access/services/config-reload';
import { FeatureManager } from '@core/data-access/services/feature-manager.service';
import { FormPrefillService } from '@core/data-access/services/form/form-prefill.service';
import { FormStepValidityService } from '@core/data-access/services/form/form-step-validity.service';
import { LanguageManager } from '@core/data-access/services/language-manager';
import { PageTitleStrategy } from '@core/data-access/services/page-title-strategy';
import { ScriptInjectionManager } from '@core/data-access/services/scripts/script-injection-manager';
import * as accessibilityEffects from '@core/data-access/store/accessibility/accessibility.effects';
import * as exhibitorEffects from '@core/data-access/store/exhibitor/exhibitor.effects';
import * as formEffects from '@core/data-access/store/form/form.effects';
import metaReducers from '@core/data-access/store/meta-reducers';
import * as prepareOrderEffects from '@core/data-access/store/prepare-order/prepare-order.effects';
import * as routerEffects from '@core/data-access/store/router/router.effects';
import * as sharedEffects from '@core/data-access/store/shared/shared.effects';
import * as buyerInformationEffects from '@core/features/buyer-information/store/buyer-information.effects';
import * as ticketAssignmentEffects from '@core/features/ticket-assignment/store/ticket-assignment.effects';
import { LoginGuardAdmin } from '@core/guards/admin-permission.guard';
import { MaintenancePageGuard } from '@core/guards/maintenance-page.guard';
import { StepPermissionGuard } from '@core/guards/step-permission.guard';
import { LoginGuardSignedOut } from '@core/guards/user-logout.guard';
import { LoginGuardUser } from '@core/guards/user-permission.guard';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { headerInterceptor } from '@core/interceptors/header.interceptor';
import { provideAppInitializationServices } from '@core/providers/app-initialization-services.provider';
import { provideAppInitializeAction } from '@core/providers/app-initializer-action.provider';
import { provideFormMediator } from '@core/providers/form-mediator.provider';
import { provideOperatorScriptsAction } from '@core/providers/operator-scripts-action.provider';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  DEFAULT_LANGUAGE,
  MissingTranslationHandler,
  provideTranslateService,
  TranslateParser
} from '@ngx-translate/core';
import { MissingTranslation } from '@shared/missing-translation/missing-translation';
import { MissingTranslationService } from '@shared/missing-translation/missing-translation.service';
import { FilterPlaceholderPipe } from '@shared/pipes/filter-placeholder/filter-placeholder.pipe';
import { CustomParser } from '@shared/translate-parser/translate-parser';
import { environment } from '@src/environments/environment';
import { AdditionalServicesEffect } from '@store/additional-services/additional-services.effects';
import { CustomizationEffect } from '@store/customization/customization.effects';
import { ExhibitionEffect } from '@store/exhibition/exhibition.effects';
import * as helperEffects from '@store/helpers/helper.effects';
import { LegitimationEffect } from '@store/legitimation/legitimation.effects';
import * as bookingEffects from '@store/products/booking/booking.effects';
import * as holderEffects from '@store/products/holder/holder.effects';
import * as productSelectionEffects from '@store/products/product-selection/product-selection.effects';
import * as productEffects from '@store/products/product.effects';
import { ArticleStatusEffects } from '@store/products/status/article/article-status.effects';
import * as packageStatusEffects from '@store/products/status/package/package-status.effects';
import * as tariffStatusEffects from '@store/products/status/tariff/tariff-status.effects';
import * as workshopStatusEffects from '@store/products/status/workshop/workshop-status.effects';
import { WorkshopEffect } from '@store/products/workshop/workshop.effects';
import * as stepsFormsEffect from '@store/step-forms/steps-forms.effects';
import { TranslationsEffects } from '@store/translation/translations.effects';
import { UserEffect } from '@store/user/user.effects';
import { provideQuillConfig } from 'ngx-quill';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

export const provideCore = () => [
  provideZoneChangeDetection({ eventCoalescing: true }),
  provideAnimationsAsync(),
  provideRouter(
    routes,
    withComponentInputBinding(),
    withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })
  ),
  provideHttpClient(withInterceptors([headerInterceptor, authInterceptor]), withInterceptorsFromDi()),
  provideAppInitializeAction(),
  provideOperatorScriptsAction(),
  provideAppInitializationServices([
    AccessibilityService,
    LanguageManager,
    FeatureManager,
    ScriptInjectionManager,
    FormPrefillService,
    FormStepValidityService,
    ConfigReload
  ]),
  provideFormMediator(),
  provideLimestone(),
  provideTranslateService({
    parser: { provide: TranslateParser, useClass: CustomParser },
    missingTranslationHandler: {
      provide: MissingTranslationHandler,
      useClass: MissingTranslation,
      deps: [MissingTranslationService]
    }
  }),
  provideQuillConfig({
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        ['link']
      ]
    },
    theme: 'snow'
  }),
  provideStore(reducers, {
    metaReducers,
    runtimeChecks: {
      strictStateImmutability: false,
      strictActionImmutability: false
    }
  }),
  provideRouterStore(),
  provideEffects(
    appEffects,
    accessibilityEffects,
    CustomizationEffect,
    ExhibitionEffect,
    helperEffects,
    buyerInformationEffects,
    ticketAssignmentEffects,
    LegitimationEffect,
    PressNewsEffect,
    UserEffect,
    ColorizerEffect,
    stepsFormsEffect,
    productSelectionEffects,
    WorkshopEffect,
    exhibitorEffects,
    formEffects,
    routerEffects,
    sharedEffects,
    prepareOrderEffects,
    bookingEffects,
    holderEffects,
    AdditionalServicesEffect,
    ArticleStatusEffects,
    packageStatusEffects,
    tariffStatusEffects,
    workshopStatusEffects,
    productEffects,
    TranslationsEffects
  ),
  provideStoreDevtools({
    maxAge: 50,
    logOnly: environment.production
  }),
  importProvidersFrom(
    NgxSkeletonLoaderModule.forRoot({
      count: 1,
      appearance: 'line',
      animation: 'progress',
      theme: {
        'background-color': '#ccc',
        'border-radius': 10
      }
    })
  ),
  { provide: APP_BASE_HREF, useValue: '/' },
  { provide: DEFAULT_LANGUAGE, useValue: 'en' },
  { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  { provide: TitleStrategy, useClass: PageTitleStrategy },
  LoginGuardUser,
  LoginGuardSignedOut,
  MaintenancePageGuard,
  StepPermissionGuard,
  LoginGuardAdmin,
  DatePipe,
  FilterPlaceholderPipe
];
